import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Label from 'components/atoms/Label';
import ToggleButton from 'components/atoms/ToggleButton';
import { USER_ROLES, USER_TYPES } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { portList } from 'services/ports';
import { userPortModify } from 'services/users';
import { setErrorHandlerAction, SetErrorHandlerAction } from 'store/actions/errorsActions';
import { PortType, RootState, UserType } from 'types';
import { ActionsType } from 'types/system';
import { renderError, setKey } from 'utils/formHandlers';
import { formatServerErrors } from 'utils/formatServerErrors';
import { translate } from 'utils/translate';

interface CreateUserPortModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    id?: string;
    data: any;
    port: PortType;
    user: UserType;
    actions: ActionsType | undefined;
    setErrorHandlerAction: SetErrorHandlerAction;
}

interface CreateUserPortModelState {
    errors: object;
    data: any | null;
    ports: PortType[];
}

class CreateUserPortModel extends Component<CreateUserPortModelProps, CreateUserPortModelState> {
    constructor(props: CreateUserPortModelProps) {
        super(props);
        this.state = {
            data: {
                user_id: props.data?.user?.id,
                port_id: 0,
                user_type: '',
                rules: [],
            },
            errors: {},
            ports: [],
        };
    }

    componentDidMount(): void {
        this.getData();
    }

    handleChange = (name: string, value: unknown) => {
        const { data, errors } = this.state;
        setKey(data, name, value);
        setKey(errors, name, null);
        this.setState({ data, errors });
    };

    setRule = (fn) => {
        const temp = this.state.data;
        if (temp['rules'].indexOf(fn) >= 0) {
            temp['rules'].splice(temp['rules']?.indexOf(fn), 1);
        } else {
            temp['rules'].push(fn);
        }
        this.setState({ data: temp });
    };

    handelPrivilegechange = (name: string, value: unknown) => {
        const temp = this.state.data;
        temp[name] = value;
        //@ts-ignore
        temp['rules'] = USER_TYPES.find((x) => {
            return x.id == value;
        }).rules;
        this.setState({ data: temp });
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();

        userPortModify(this.props.port.id, 0, this.state.data)
            .then(() => {
                this.props.actions?.onConfirm();
                this.props.actions?.onCancel();
            })
            .catch((errors) => {
                switch (errors.response?.status) {
                    case 422:
                        this.setState({ errors: formatServerErrors(errors) });
                        break;
                    default:
                        this.props.setErrorHandlerAction(errors);
                        break;
                }

                return;
            });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    getData = () => {
        portList()
            .then((res) => {
                this.setState({
                    ports: res.items,
                });
            })
            .catch((error) => {
                this.props.setErrorHandlerAction(error);
            });
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-4 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Add user ports')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>

                    <FormGroup comments={renderError(this.state.errors?.['port_id'])} required={false}>
                        <Label>{translate('port')}</Label>
                        <DropList
                            capitalize
                            name="port_id"
                            id="port_id"
                            translate={false}
                            value={this.state.data?.port_id}
                            options={this.state.ports
                                .filter((x) => {
                                    if (this.props.user.type === 'systemAdministrator') {
                                        return true;
                                    }
                                    return this.props.user.user_ports
                                        .map((y) => {
                                            return y.port_id;
                                        })
                                        .includes(x.id);
                                })
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((x) => {
                                    return { value: x.id, label: x.name };
                                })}
                            onChange={this.handleChange}
                        ></DropList>
                    </FormGroup>

                    <FormGroup comments={renderError(this.state.errors?.['user_type'])} required={false}>
                        <Label>{translate('title')}</Label>
                        <DropList
                            capitalize
                            name="user_type"
                            id="user_type"
                            //@ts-ignore
                            value={this.state.data?.user_type}
                            options={USER_TYPES.map((x) => {
                                return { value: x.id, label: x.name };
                            })}
                            onChange={this.handelPrivilegechange}
                        ></DropList>
                    </FormGroup>

                    <div className="mt-4">
                        <Label>{translate('Privileges')}</Label>
                        {USER_ROLES.map((rule, index) => {
                            return (
                                <div key={index} className="mt-1">
                                    <div className="flex">
                                        <div className="cursor-pointer  rounded border-gray-300">
                                            <ToggleButton
                                                onChange={() => {
                                                    this.setRule(rule.function);
                                                }}
                                                value={
                                                    this.state.data?.rules.indexOf(rule.function) >= 0 ? true : false
                                                }
                                            ></ToggleButton>
                                        </div>
                                        <div className="">
                                            <div className="font-bold">{rule.description}</div>
                                            <div className="text-gray-400">{rule.help}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-end">
                        <div className="mt-2 gap-3 flex">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const createUserPortModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    port: state.port.port,
    user: state.user.user,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createUserPortModelDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(createUserPortModelStateToProps, createUserPortModelDispatchToProps)(CreateUserPortModel);
